"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  CountGrid,
  parseCounts,
  replaceAt,
  replaceCell,
} from "@/components/tools/stats-tests/count-grid";
import { FisherResults } from "@/components/tools/stats-tests/fisher-results";
import {
  alphaOptions,
  ExampleButton,
  levelOptions,
} from "@/components/tools/stats-tests/kit";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { fisherExact } from "@/lib/tool-kit/stats-tests/fisher-exact";
import type { Alternative } from "@/lib/tool-kit/stats-tests/format";

/** English strings. A translated content file overrides these through `ui`. */
export const FISHER_EXACT_TEST_CALCULATOR_STRINGS = {
  tableHint:
    "Type the four counts. Rename the rows (groups) and columns (outcomes) to label the results.",
  cell: "Count for {row}, {column}",
  rowName: "Name of row {n}",
  columnName: "Name of column {n}",
  total: "Total",
  rowDefault: "Group {n}",
  columnDefault: "Outcome {n}",
  alternativeLabel: "Alternative hypothesis",
  twoSided: "Two-sided: odds ratio ≠ 1",
  less: "Odds ratio < 1",
  greater: "Odds ratio > 1",
  alternativeHint:
    "The odds ratio compares the odds of column 1 in row 1 with the odds of column 1 in row 2.",
  alphaLabel: "Significance level (α)",
  levelLabel: "Confidence level",
  tryExample: "Try an example",
  exampleRows: "Tutoring|No tutoring",
  exampleColumns: "Passed|Failed",
  exampleCounts: "8, 2, 1, 5",
  results: "Results",
  pTwoSided: "p-value (two-sided)",
  pLess: "p-value (odds ratio < 1)",
  pGreater: "p-value (odds ratio > 1)",
  sampleOr: "Sample odds ratio (ad / bc)",
  conditionalOr: "Conditional MLE odds ratio",
  ci: "{level} exact CI for the odds ratio",
  pObserved: "Probability of this exact table",
  alternativeColumn: "Alternative",
  exactColumn: "Exact p",
  apaColumn: "APA",
  n: "Total (N)",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the outcome is associated with the group.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence of an association.",
  orNote:
    "Two odds ratios, because packages differ: the sample odds ratio ad / bc (SciPy, most textbooks) and the conditional maximum-likelihood estimate (R's fisher.test), which the exact interval belongs to.",
  distributionHeading: "Every table with these margins",
  distributionHint:
    "With the row and column totals fixed, the top-left count a determines the whole table. The two-sided p adds up every table no more likely than yours.",
  aColumn: "a (top-left count)",
  probabilityColumn: "P(table)",
  includedColumn: "In two-sided p",
  yes: "yes",
  no: "no",
  observedMark: "{value} (observed)",
  distributionHidden:
    "The table of possible tables is shown when there are 40 or fewer.",
  howCalculated: "How this was calculated",
  workingTwoSided: "Two-sided p = {sum}",
  apaHeading: "APA-style result",
  apaTemplate:
    "Fisher's exact test, *p* {p}, *OR* = {or}, {level} CI [{low}, {high}]",
  apaNote:
    "OR is the conditional maximum-likelihood estimate, which matches the exact interval; give the counts or percentages for each group as well.",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt: "Fill all four cells with whole-number counts — zero is allowed.",
  errorMargin:
    "A row or column adds up to zero, so there is nothing to test. Every row and column needs at least one count.",
} as const;

const SPLIT = "|";

function labels(template: string): string[] {
  return [t(template, { n: 1 }), t(template, { n: 2 })];
}

export default function FisherExactTestCalculatorWidget() {
  const strings = useToolUi(FISHER_EXACT_TEST_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [rowLabels, setRowLabels] = useState(() => labels(strings.rowDefault));
  const [colLabels, setColLabels] = useState(() =>
    labels(strings.columnDefault)
  );
  const [cells, setCells] = useState<string[][]>([
    ["", ""],
    ["", ""],
  ]);
  const [alternative, setAlternative] = useState<Alternative>("two-sided");
  const [alpha, setAlpha] = useState("0.05");
  const [level, setLevel] = useState("0.95");

  const table = parseCounts(cells);
  const result = table
    ? fisherExact(
        table[0][0],
        table[0][1],
        table[1][0],
        table[1][1],
        Number(level)
      )
    : null;
  const alternatives: Option<Alternative>[] = [
    { value: "two-sided", label: strings.twoSided },
    { value: "less", label: strings.less },
    { value: "greater", label: strings.greater },
  ];

  function fillExample() {
    const [a = "", b = "", c = "", d = ""] = strings.exampleCounts
      .split(",")
      .map((value) => value.trim());
    setCells([
      [a, b],
      [c, d],
    ]);
    setRowLabels(strings.exampleRows.split(SPLIT));
    setColLabels(strings.exampleColumns.split(SPLIT));
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className={HINT}>{strings.tableHint}</p>
        <CountGrid
          colLabels={colLabels}
          fmt={fmt}
          idPrefix="fisher"
          onCell={(i, j, value) => setCells(replaceCell(cells, i, j, value))}
          onColLabel={(j, value) =>
            setColLabels(replaceAt(colLabels, j, value))
          }
          onRowLabel={(i, value) =>
            setRowLabels(replaceAt(rowLabels, i, value))
          }
          rowLabels={rowLabels}
          text={strings}
          values={cells}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <SelectField
          hint={strings.alternativeHint}
          id="fisher-alternative"
          label={strings.alternativeLabel}
          onChange={setAlternative}
          options={alternatives}
          value={alternative}
        />
        <SelectField
          id="fisher-alpha"
          label={strings.alphaLabel}
          onChange={setAlpha}
          options={alphaOptions(fmt)}
          value={alpha}
        />
        <SelectField
          id="fisher-level"
          label={strings.levelLabel}
          onChange={setLevel}
          options={levelOptions(fmt)}
          value={level}
        />
      </div>
      <ExampleButton label={strings.tryExample} onClick={fillExample} />
      {result ? (
        <FisherResults
          alpha={Number(alpha)}
          alternative={alternative}
          fmt={fmt}
          r={result}
          strings={strings}
        />
      ) : (
        <Notice>{table ? strings.errorMargin : strings.prompt}</Notice>
      )}
    </div>
  );
}
